import { jslog } from '@gd/jslog'

/*
 *  Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */

/**
 * This class encapsulates the protocol logic for talking to the matching backend via websocket.
 * Clients of this class only need to call the start function and then use the returned Promise.
 */
export default class MatchingWebsocketClient {
  static readonly HEARTBEAT_INTERVAL: number = 45 * 1000
  static readonly EXPECT_HEARTBEAT_RESPONSE_TIME = 20 * 1000

  public updateUserCountCallback: (count: number) => void

  private backendHostname: string
  private slotId: string
  private ticket: string

  private websocket: WebSocket
  private requestId: number
  private keepAliveHandle: number

  private receivedStartCallback: (obj: { config: string; digest: string }) => void
  private matchingCancelledCallback: (s: string) => void

  private navigatorImpl: Navigator

  // only the data, not the full MessageEvent
  private lastSimpleOkEvent: any

  /**
   *
   * @param {object} matchingData - the data we need for matching, is passed to the backend
   * @param {string} matchingData.slotId - the slot id
   * @param {string} matchingData.ticket - the ticket for joining the matching lobby
   * @param {object} testInstrumentation - an optional object, holding mockec dependencies of this class for testing
   * @param {WebSocket} testInstrumentation.websocketMock - for unit testing, pass in a mocked websocket (the socket will be treated by this
   * class as its own personal websocket, so websocket sharing is not advised)
   * @param {Function} testInstrumentation.navigatorMock - for unit testing, pass in a mocked version of Navigator
   */
  constructor({ slotId, ticket }: { slotId: string; ticket: string }, testInstrumentation?: any) {
    this.slotId = slotId
    this.ticket = ticket

    if (testInstrumentation) {
      this.websocket = testInstrumentation.websocketMock
      this.navigatorImpl = testInstrumentation.navigatorMock
    } else {
      // no need to set this.websocket here, start function will instantiate real runtime version of
      // websocket if none has been set from testInstrumentation
      this.navigatorImpl = navigator
    }
  }

  /**
   * Start the matching protocol.
   *
   * @param backendHostname hostname of the matching backend
   * @returns {Promise} a promise that on fulfillment, matching was either successful or somehow cancelled. If cancelled,
   * Promise.error will be called with a "reason" parameter.
   */
  start(backendHostname: string): Promise<object> {
    this.backendHostname = backendHostname
    return new Promise((receivedStart, matchingCancelled) => {
      this.receivedStartCallback = receivedStart
      this.matchingCancelledCallback = matchingCancelled

      // skip websocket instantiation if a (possibly mocked) instance has been passed in with the constructor
      if (!this.websocket) {
        const proto = 'https:' === location.protocol ? 'wss:' : 'ws:'
        this.websocket = new WebSocket(`${proto}//${backendHostname}/gameserver`)
      }
      this.requestId = 1

      this.websocket.onopen = () => this.handleOnOpen()

      this.websocket.onmessage = (messageEvent: MessageEvent) => this.handleOnMessage(messageEvent)

      // for next 2 cases, we must inform backend via REST that we're leaving + purge socket
      this.websocket.onclose = (closeEvent: CloseEvent) => {
        // See https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code about the closing codes

        // let's only log closing where the code is not 400x, meaning the closing was not triggered by our application
        if (closeEvent.code < 4000) {
          jslog.send({
            name: 'tournament-matching-websocket-closed',
            data: { closeCode: closeEvent.code },
          })
        }
        if (this.websocket) {
          // this.websocket is set to null when the websocket is closed by us (e.g. see handleBackendSentStartGame).
          // however, onclose is still called even if we closed it ourselves so we have to ensure we're not doing
          // any protocol parts pertaining to somebody else closing the socket
          this.shutdown(true, 4001)
          this.matchingCancelledCallback('websocket_closed')
        }
      }
      this.websocket.onerror = (event) => {
        if (event instanceof ErrorEvent) {
          jslog.send({ error: (<ErrorEvent>event).error })
        } else {
          jslog.send({ error: new Error('tournaments-matching-websocket-error') })
        }
        this.shutdown(true, 4001)
        this.matchingCancelledCallback('websocket_error')
      }
    })
  }

  cancelMatching() {
    this.shutdown(true, 4001)
  }

  /**
   * Stops the heartbeat, closes the websocket (if not already closed) and informs matching backend via REST
   * call (if needed/requested).
   *
   * @param leaveByRestCall if true there will be a REST call to lobby backend so that user is removed from matching
   * @param wsCloseCode one of 4000 (game starts) or 4001 for all other cases (cancelling by user or from errors). if no code is
   *  passed no code will be used when calling WebSocket.close.
   */
  private shutdown(leaveByRestCall?: boolean, wsCloseCode?: number): void {
    this.stopHeartbeat()

    // see 5 lines below: we're setting the websocket member to null ourselves as a marker for "assume ws is not open"
    if (this.websocket) {
      if (this.websocket.readyState == WebSocket.OPEN) {
        this.websocket.close(wsCloseCode)
      }
      this.websocket = null
    }

    if (leaveByRestCall) {
      this.informBackendAboutLeavingNonWs()
    }
  }

  private informBackendAboutLeavingNonWs() {
    // we're using the "los" event here to signal to the lobby backend that we're leaving even though "los" is
    // the "connection lost" event which is not really the case here.
    // but the advantage of using "los" is that other more appropriate event (at least semantically more appropriate)
    // will trigger a re-check of the connection from backend which we don't need/want here
    const payload = {
      eventName: 'los',
      slotId: this.slotId,
      ticket: this.ticket,
      requestId: this.requestId++,
    }
    const host = '//' + this.backendHostname + '/gameserver-rest'
    this.navigatorImpl.sendBeacon(host, JSON.stringify(payload))
  }

  private handleOnOpen() {
    this.websocket.send(this.toJsonifiedEvent('ips'))
    // this go_in is the first real message and should have the 1
    this.requestId = 1
    this.websocket.send(this.toJsonifiedEvent('go_in', [this.ticket]))
    this.startHeartbeat()
  }

  private handleOnMessage(event: MessageEvent) {
    // see https://confluence.gameduell.de/pages/viewpage.action?spaceKey=GAME&title=GameMastersLobbyProtocolDoc
    const eventData = JSON.parse(event.data)
    if (this.isSimpleOkEvent(eventData)) {
      this.lastSimpleOkEvent = eventData
      return
    } else if (!eventData.name) {
      console.warn('No name property in MessageEvent.data, skipping')
      return
    }

    const eventName = eventData.name.toLowerCase()
    switch (eventName) {
      case 'start':
        this.handleBackendSentStartGame(eventData.data)
        break
      case 'leave':
        // "The user should leave the lobby for whatever reason"
        this.shutdown(true, 4001)
        this.matchingCancelledCallback('server_sent_leave')
        break
      case 'closed':
        // "send to the players when the lobby is shut down and the play hasn't been matched yet"
        this.shutdown(true, 4001)
        this.matchingCancelledCallback('server_sent_closed')
        break
      case 'users':
        this.updateUserCountCallback && this.updateUserCountCallback(eventData.data)
        // we're not interested in that one: matching page will *not* show any progress like "we matched another player"
        // because matching is atomic
        break
      default:
        jslog.send({
          error: new Error('tournament-matching-unknown-wsevent'),
          data: { unknownEvent: eventName, eventData: eventData, fullEvent: JSON.stringify(event) },
        })
    }
  }

  private isSimpleOkEvent(eventData: any): boolean {
    return (!eventData.name || eventData.length === 0) && eventData.state && eventData.state.toLowerCase() === 'ok'
  }

  /**
   *
   * @param startGameParams the params
   * @returns {Array<string>} the array of 2 params or undefined (if somehow incomplete)
   */
  private startParamsToArray(startGameParams: string): Array<string> {
    let paramsList = startGameParams && startGameParams.length > 0 ? startGameParams.split('\t') : undefined
    if (!paramsList) {
      jslog.send({ error: new Error('tournament-matching-no-startgame-params') })
      return undefined
    }

    paramsList = paramsList.filter((entry) => entry && entry.length > 0)

    if (Object.values(paramsList).length < 2) {
      jslog.send({ error: new Error('tournament-matching-startgame-params-missing-or-empty') })
      return undefined
    }

    return paramsList
  }

  private handleBackendSentStartGame(startGameParams: any) {
    const startGameParamsList = this.startParamsToArray(startGameParams)
    if (!startGameParamsList) {
      this.shutdown(true, 4001)
      this.matchingCancelledCallback('invalid_event_from_backend')
      return
    }

    // send leave - even if this is very probably not needed because there is an unload listener in MMatching.vue - but the event
    // is idempotent so extra calls will do no harm
    this.websocket.send(this.toJsonifiedEvent('leave'))
    this.shutdown(false, 4000)
    this.receivedStartCallback({
      config: startGameParamsList[0],
      digest: startGameParamsList[1],
    })
  }

  private startHeartbeat() {
    this.keepAliveHandle = window.setTimeout(() => {
      const requestId = this.sendHeartbeat()
      this.checkHeartbeatResponseArrived(requestId)
        .then(() => {
          this.startHeartbeat()
        })
        .catch(() => {
          jslog.send({ error: new Error('tournament-matching-no_heartbeat_response') })
          this.shutdown(true, 4001)
          this.matchingCancelledCallback('connection_lost')
        })
    }, MatchingWebsocketClient.HEARTBEAT_INTERVAL)
  }

  private checkHeartbeatResponseArrived(expectedRequestId: number): Promise<void> {
    return new Promise((responseArrived, noResponse) => {
      setTimeout(() => {
        if (this.lastSimpleOkEvent && this.lastSimpleOkEvent.requestId == expectedRequestId) {
          responseArrived()
        } else {
          this.do2nTryHeartbeatCheck()
            .then(() => {
              responseArrived()
            })
            .catch(() => {
              noResponse()
            })
        }
        this.lastSimpleOkEvent = null
      }, MatchingWebsocketClient.EXPECT_HEARTBEAT_RESPONSE_TIME)
    })
  }

  private do2nTryHeartbeatCheck(): Promise<void> {
    const requestId = this.sendHeartbeat()
    return new Promise((responseArrived, noResponse) => {
      setTimeout(() => {
        if (this.lastSimpleOkEvent && this.lastSimpleOkEvent.requestId == requestId) {
          responseArrived()
        } else {
          noResponse()
        }
      }, MatchingWebsocketClient.EXPECT_HEARTBEAT_RESPONSE_TIME)
    })
  }

  /**
   * @returns {number} - the requestId used for the heartbeat event
   */
  private sendHeartbeat(): number {
    const keepAliveEvent = this.toEvent('keep_alive')
    this.websocket.send(JSON.stringify(keepAliveEvent))
    return keepAliveEvent.requestId
  }

  private stopHeartbeat() {
    if (this.keepAliveHandle) {
      window.clearTimeout(this.keepAliveHandle)
      this.keepAliveHandle = null
    }
  }

  private toEvent(eventName: string, payload?: Array<any>): any {
    return {
      eventName: eventName,
      payload: payload !== undefined ? payload.join('\t') : '',
      slotId: this.slotId,
      ticket: this.ticket,
      requestId: this.requestId++,
    }
  }

  private toJsonifiedEvent(eventName: string, payload?: Array<any>): string {
    return JSON.stringify(this.toEvent(eventName, payload))
  }
}
